/**
 * `suggest` reducer — typeahead results. (item 3)
 *
 * Two properties a typeahead reducer has to have, and neither is free:
 *
 * OUT-OF-ORDER RESPONSES ARE DROPPED. Every keystroke can be in flight at
 * once, and they do not come back in the order they were sent — a request for
 * "doe" issued before one for "doe-std" can land after it. Without a guard the
 * panel would flick back to the shorter prefix's results and, worse, would
 * sometimes SETTLE there. So each action carries the term it was issued for
 * and a SUCCESS is applied only when that term is still the one being asked
 * about. Debouncing reduces how often this happens; it does not prevent it.
 *
 * THE PREVIOUS LIST SURVIVES A PENDING. Clearing on `_PENDING` makes the panel
 * blink empty between keystrokes, which reads as "no matches" at exactly the
 * moment the user is typing the thing that will match. Same
 * stale-while-revalidate reasoning as the querystringsearch reducer next door.
 *
 * Registered through `config.addonReducers`.
 */
import { GET_SUGGESTIONS } from '../actions/suggest';

const initialState = {
  error: null,
  items: [],
  loaded: false,
  loading: false,
  // The term the CURRENT items describe — read by the hook so it can tell
  // "no matches for what you typed" from "no matches for a stale prefix".
  term: '',
  // The term of the most recent request, whether or not it has landed.
  pendingTerm: '',
};

export default function suggest(state = initialState, action = {}) {
  switch (action.type) {
    case `${GET_SUGGESTIONS}_PENDING`:
      return {
        ...state,
        error: null,
        loading: true,
        loaded: false,
        pendingTerm: action.term ?? state.pendingTerm,
        // items deliberately untouched — see the header.
      };

    case `${GET_SUGGESTIONS}_SUCCESS`: {
      // Late response for a prefix the user has already typed past.
      if (action.term !== state.pendingTerm) return state;
      return {
        ...state,
        error: null,
        loading: false,
        loaded: true,
        term: action.term ?? '',
        items: action.result?.suggestions || [],
      };
    }

    case `${GET_SUGGESTIONS}_FAIL`:
      if (action.term !== state.pendingTerm) return state;
      return {
        ...state,
        error: action.error,
        loading: false,
        loaded: false,
        items: [],
      };

    default:
      return state;
  }
}
